/*
 * Premium motion — layer 3 of 3: navigation.
 *
 * Draws a column curtain across internal navigations and clears it on arrival,
 * and warms the next page up before it is asked for.
 *
 * The curtain does not hold the navigation up. It used to: every internal link
 * was preventDefault'd, the curtain played to completion, and only then was
 * `window.location.href` set — so every click on the store cost the full cover
 * animation before the browser had even asked for the next page, and then paid
 * for the lift on arrival on top of that. The animation was the navigation's
 * latency rather than a cover for it.
 *
 * A browser keeps painting the current document until the next one is ready to
 * paint, so the curtain plays over the fetch for free. Letting the click
 * through unchanged and painting on top gets the same picture with none of the
 * wait — and it means a page already in the prefetch cache appears immediately
 * rather than after a fixed animation.
 *
 * Two rules still govern everything here: never swallow a navigation, and never
 * leave the curtain down. Nothing is intercepted now, so the first is free; a
 * timer and `pageshow` handle the second for the cases where a click starts a
 * navigation that never completes.
 */
(function () {
  'use strict';

  const root = document.documentElement;

  if (!root.classList.contains('pm-motion')) return;
  if (window.Shopify && window.Shopify.designMode) return;

  const COLUMNS = 9;
  /* These two have to stay in step with the transition in premium-transitions.css. */
  const COLUMN_DURATION_MS = 380;
  const COLUMN_STAGGER_MS = 26;
  const COVER_MS = COLUMN_DURATION_MS + COLUMNS * COLUMN_STAGGER_MS;
  /*
   * A click can start a navigation that never lands — a link the browser turns
   * into a download, a cancelled request, a 204. The page is still here, so the
   * curtain has to come back up rather than sit there black.
   */
  const STUCK_MS = 4000;

  let curtain = null;
  let navigating = false;
  let stuckTimer = 0;

  function build() {
    curtain = document.createElement('div');
    curtain.className = 'pm-curtain';
    curtain.setAttribute('aria-hidden', 'true');
    curtain.setAttribute('data-pm-curtain', 'idle');

    for (let i = 0; i < COLUMNS; i += 1) {
      const column = document.createElement('div');
      column.className = 'pm-curtain__col';
      column.style.setProperty('--pm-col-delay', `${i * COLUMN_STAGGER_MS}ms`);
      curtain.appendChild(column);
    }

    document.body.appendChild(curtain);
  }

  function setState(state) {
    if (!curtain) return;
    curtain.setAttribute('data-pm-curtain', state);
  }

  /* --------------------------------------------------------------- arrival */

  /*
   * A curtain is worth drawing over a wait. Over no wait it is the wait.
   *
   * With the next page prefetched, a click can land on a document that is
   * already in the cache and paints immediately - and covering that in black
   * for half a second to uncover it again is slower than doing nothing, and
   * reads as a flash rather than a transition. So the arrival curtain is only
   * played when the page actually took long enough to be worth hiding.
   */
  function arrivedInstantly() {
    if (!window.performance || !performance.getEntriesByType) return false;

    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav) return false;

    // Chrome labels a navigation it served out of the prefetch cache.
    if (nav.deliveryType === 'navigational-prefetch') return true;
    // Back and forward come out of bfcache or the HTTP cache; they are instant
    // by nature and the browser already animates them on mobile.
    if (nav.type === 'back_forward') return true;

    // Otherwise: how long the document itself took to arrive.
    const documentTime = nav.responseEnd - nav.fetchStart;
    return documentTime > 0 && documentTime < 150;
  }

  function playArrival() {
    if (!curtain) return;

    // Start covered, then clear on the next frame so the transition has two
    // distinct states to animate between.
    setState('in');

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setState('out');
        window.setTimeout(() => setState('idle'), COVER_MS);
      });
    });
  }

  /* ------------------------------------------------------- link eligibility */

  /*
   * Whether this link goes to another page of this store under its own steam.
   * Shared by the curtain and the prefetcher, so the two can never disagree
   * about what counts as a navigation.
   */
  function isInternalNavigation(link) {
    if (!link || !link.href) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.hasAttribute('data-no-transition')) return false;

    const raw = link.getAttribute('href');
    if (!raw || raw.charAt(0) === '#') return false;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }

    if (url.origin !== window.location.origin) return false;
    if (!/^https?:$/.test(url.protocol)) return false;

    // Same page, different hash — that is an in-page jump, not a navigation.
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
      if (url.hash) return false;
    }

    // Anything the theme handles with its own JS keeps its behaviour.
    if (link.closest('cart-drawer, .cart-drawer, .menu-drawer, predictive-search')) return false;
    if (link.closest('[data-no-transition]')) return false;

    return true;
  }

  function shouldCover(link, event) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    // Modified clicks open a new tab or window; this one stays put.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    return isInternalNavigation(link);
  }

  /* ------------------------------------------------------------- departure */

  function depart() {
    if (navigating) return;
    navigating = true;

    setState('in');

    stuckTimer = window.setTimeout(() => {
      navigating = false;
      setState('idle');
    }, STUCK_MS);
  }

  function release() {
    window.clearTimeout(stuckTimer);
    navigating = false;
  }

  /* ------------------------------------------------------------- prefetch */

  /*
   * Warm the next document before it is asked for. Most of what a click costs
   * on a Shopify storefront is the round trip for the HTML, and hovering a link
   * is a reliable signal that it is about to be clicked.
   *
   * Chrome and Edge get this from the speculation rules block in theme.liquid,
   * which handles hover intent, touch, and its own cache accounting far better
   * than this can. This is the fallback for the browsers that do not support
   * it but do support `rel=prefetch`, and it stands down where the rules are
   * already running so nothing is fetched twice.
   */
  function initPrefetch() {
    const supportsSpeculationRules =
      typeof HTMLScriptElement !== 'undefined' &&
      typeof HTMLScriptElement.supports === 'function' &&
      HTMLScriptElement.supports('speculationrules');
    if (supportsSpeculationRules) return;

    // rel=prefetch is a hint, not a promise. If the browser does not recognise
    // it the request never happens, so do not spend the listeners.
    const probe = document.createElement('link');
    if (!probe.relList || !probe.relList.supports || !probe.relList.supports('prefetch')) return;

    // Someone on a metered connection did not ask for pages they may not visit.
    const connection = navigator.connection;
    if (connection) {
      if (connection.saveData) return;
      if (/(^|-)2g$/.test(connection.effectiveType || '')) return;
    }

    const done = new Set();
    /*
     * Checkout and account pages are personalised or start a session, and the
     * cart is re-read on arrival anyway - none of them are worth warming, and
     * checkout in particular should not be touched speculatively.
     */
    const SKIP = /^\/(checkouts?|account|cart\/|orders|apps\/)/;

    function warm(link) {
      if (!isInternalNavigation(link)) return;

      const url = new URL(link.href, window.location.href);
      const key = url.pathname + url.search;
      if (done.has(key)) return;
      if (SKIP.test(url.pathname)) return;
      if (link.hasAttribute('data-no-prefetch')) return;

      done.add(key);

      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url.pathname + url.search;
      hint.as = 'document';
      document.head.appendChild(hint);
    }

    function onIntent(event) {
      const link = event.target.closest && event.target.closest('a[href]');
      if (link) warm(link);
    }

    // pointerenter does not bubble; pointerover does, so one listener covers
    // every link on the page including the ones added later.
    document.addEventListener('pointerover', onIntent, { passive: true });
    // Touch has no hover. touchstart still lands a beat before the click.
    document.addEventListener('touchstart', onIntent, { passive: true });
  }

  /* ------------------------------------------------------------ bootstrap */

  function init() {
    build();
    // The curtain is already `idle`, so skipping this leaves the page uncovered.
    if (!arrivedInstantly()) playArrival();

    document.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;
      if (!shouldCover(link, event)) return;

      /*
       * Deliberately no preventDefault. The browser starts fetching the next
       * document on this click and keeps painting this one until that document
       * is ready, so the curtain covers the wait rather than adding to it.
       */
      depart();
    });

    initPrefetch();
  }

  // Restoring from bfcache repaints the covered curtain — clear it.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    release();
    playArrival();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
